import { ReactNode, useState } from "react";
import { ArrowRightIcon, BookOpenIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";

import { PinnacleItemTypeStatus, PinnaclePartStatus } from "../../models/PinnacleMapping/PinnacleMapping";
import { itemStatusMeta, partStatusMeta } from "./pinnacleMeta";

const OPEN_KEY = "pinnacle-mapping:guide-open";

function readOpen() {
  try {
    // Open until someone closes it, so a first visit starts with the guide.
    return localStorage.getItem(OPEN_KEY) !== "false";
  } catch {
    return true;
  }
}

function rememberOpen(open: boolean) {
  try {
    localStorage.setItem(OPEN_KEY, String(open));
  } catch {
    // Storage blocked: it just opens again next time.
  }
}

const TAB_KEY = "pinnacle-mapping:guide-tab";

type TabId = "overview" | "setup" | "map" | "move" | "push" | "words" | "labels";

const tabs: { id: TabId; label: string }[] = [
  { id: "overview", label: "What it does" },
  { id: "setup", label: "First time" },
  { id: "map", label: "Map an item type" },
  { id: "move", label: "Move parts" },
  { id: "push", label: "Send to customers" },
  { id: "words", label: "Words" },
  { id: "labels", label: "Labels" },
];

function readTab(): TabId {
  try {
    const saved = localStorage.getItem(TAB_KEY);
    return tabs.some((t) => t.id === saved) ? (saved as TabId) : "overview";
  } catch {
    return "overview";
  }
}

/**
 * How the mapping page works, for someone opening it for the first time. One topic per tab, in the
 * order you would do them, with a Next button to walk through.
 */
export function UserGuide() {
  const [open, setOpen] = useState(readOpen);
  const [tab, setTab] = useState<TabId>(readTab);

  const toggle = () => {
    setOpen(!open);
    rememberOpen(!open);
  };

  const choose = (id: TabId) => {
    setTab(id);
    try {
      localStorage.setItem(TAB_KEY, id);
    } catch {
      // Storage blocked: it starts on the first tab next time.
    }
  };

  const index = tabs.findIndex((t) => t.id === tab);
  const next = tabs[index + 1];

  return (
    <section className="overflow-hidden rounded-lg border border-blue-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls="pinnacle-guide"
        className="flex w-full items-center gap-3 bg-blue-50 px-4 py-3 text-left hover:bg-blue-100/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
      >
        <BookOpenIcon className="h-5 w-5 shrink-0 text-blue-600" aria-hidden />
        <span className="flex-1">
          <span className="block text-sm font-semibold text-gray-900">How this page works</span>
          <span className="block text-xs text-gray-600">
            {open ? "Hide the guide once you know your way round." : "New here? Open this first."}
          </span>
        </span>
        <ChevronDownIcon className={clsx("h-5 w-5 text-gray-500 transition-transform", open && "rotate-180")} aria-hidden />
      </button>

      {open && (
        <div id="pinnacle-guide" className="text-sm text-gray-700">
          <div role="tablist" aria-label="Guide topics" className="flex gap-1 overflow-x-auto border-b border-gray-200 px-3 pt-3">
            {tabs.map((t, i) => (
              <button
                key={t.id}
                id={`guide-tab-${t.id}`}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                aria-controls={`guide-panel-${t.id}`}
                onClick={() => choose(t.id)}
                className={clsx(
                  "-mb-px flex shrink-0 items-center gap-2 whitespace-nowrap rounded-t-md border-b-2 px-3 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500",
                  tab === t.id ? "border-orange-600 text-gray-900" : "border-transparent text-gray-500 hover:text-gray-700",
                )}
              >
                {i >= 1 && i <= 4 && (
                  <span
                    className={clsx(
                      "flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold",
                      tab === t.id ? "bg-orange-600 text-white" : "bg-gray-200 text-gray-600",
                    )}
                  >
                    {i}
                  </span>
                )}
                {t.label}
              </button>
            ))}
          </div>

          <div id={`guide-panel-${tab}`} role="tabpanel" aria-labelledby={`guide-tab-${tab}`} className="p-5">
            {tab === "overview" && <Overview />}
            {tab === "setup" && (
              <Steps
                intro="Do these once, in order, before mapping anything."
                steps={[
                  <>
                    Open <b>Set up and import</b> below and press <b>Import legacy names</b>. This copies every category's old
                    Pinnacle name across as a mapping, so imports carry on exactly as they do today.
                  </>,
                  <>
                    Press <b>Choose CSV</b> under <b>Match sheet</b> and pick <code>partcategory_pinnacle_matches.csv</code>. Its
                    rows become suggestions, not mappings.
                  </>,
                  <>
                    Press <b>Build suggestions</b>. This adds suggestions by matching names, including LH and RH versions of the
                    same part.
                  </>,
                  <>
                    Choose a customer in <b>Show parts from</b>. Any item types in their parts that aren't on the list yet are
                    added, with counts of how many parts each has.
                  </>,
                ]}
              />
            )}
            {tab === "map" && (
              <Steps
                intro="Repeat for each item type. The list starts with the ones that need work."
                steps={[
                  <>
                    Pick an item type on the left. <b>To do</b> shows only the ones that are unmapped, need checking or have
                    parts waiting.
                  </>,
                  <>
                    If it says <Pill status="NeedsCheck" />, look at the mapping copied from the old Pinnacle name. Either press{" "}
                    <b>Mark as checked</b>, or change it.
                  </>,
                  <>
                    Press <b>Add</b> on a suggestion that's right, or <b>+ Add mapping</b> to make your own. Pick the category,
                    then press <b>Add</b> or <b>Save</b> on that row.
                  </>,
                  <>
                    Where one Pinnacle item type covers several of our categories, add one mapping for each and set the{" "}
                    <b>Side</b>, <b>Position</b> or <b>Keyword</b> that tells them apart.
                  </>,
                  <>Check the <b>Affected parts</b> table underneath to see where the customer's parts would go now.</>,
                ]}
              />
            )}
            {tab === "move" && (
              <Steps
                intro="Changing a mapping never moves stock on its own. You choose what moves."
                steps={[
                  <>Choose the customer in <b>Show parts from</b> and pick the item type.</>,
                  <>
                    Read the <b>Outcome</b> column. Parts marked <PartPill status="Move" /> are ticked for you; nothing else is.
                  </>,
                  <>Untick anything you want left alone. Click a part's <b>Move to</b> cell to send it somewhere else by hand.</>,
                  <>
                    Press <b>Move ticked parts</b>. Ticking a part that's <PartPill status="Listed" /> withdraws its eBay listing
                    and builds a new offer under the new category.
                  </>,
                ]}
              />
            )}
            {tab === "push" && (
              <Steps
                intro="Mappings are shared by all customers. Customers' Cores only use them once they've been sent."
                steps={[
                  <>They go out automatically with the nightly category sync at 01:00, so usually you don't need to do anything.</>,
                  <>To send them straight away, press <b>Push mappings to customers</b> at the top, then <b>Push to all</b>.</>,
                  <>
                    From then on, each night's import files new parts with your mappings. A part no mapping fits is left
                    uncategorised and shows up here as waiting.
                  </>,
                ]}
              />
            )}
            {tab === "words" && <Terms />}
            {tab === "labels" && <Legends />}

            <div className="mt-5 flex items-center justify-between border-t border-gray-100 pt-4">
              <span className="text-xs text-gray-400">
                {index + 1} of {tabs.length}
              </span>
              {next ? (
                <button
                  type="button"
                  onClick={() => choose(next.id)}
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-sm font-medium text-orange-700 hover:bg-orange-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-600"
                >
                  Next: {next.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={toggle}
                  className="rounded px-2 py-1 text-sm font-medium text-orange-700 hover:bg-orange-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-600"
                >
                  Done, hide the guide
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Overview() {
  return (
    <div className="space-y-3">
      <p className="max-w-3xl">
        Pinnacle files every part under its own <b>item type</b>, such as <Token>HEADREST</Token>. This page says which of
        our categories each item type goes to. One Pinnacle item type often covers several of ours, so a mapping can depend
        on what the yard typed against the part, such as <i>LH</i>, <i>front</i> or <i>N/S</i>.
      </p>

      <div className="flex flex-wrap items-stretch gap-2">
        <FlowCard label="Pinnacle sends">
          <Token>HEADREST</Token>
          <span className="mt-1 block text-xs text-gray-500">"Front RH - Grey cloth"</span>
        </FlowCard>
        <Arrow />
        <FlowCard label="Your mappings">
          <span className="block text-xs text-gray-500">LH + Front → Headrest Front LH</span>
          <span className="block text-xs font-semibold text-gray-900">RH + Front → Headrest Front RH ✓</span>
          <span className="block text-xs text-gray-500">Rear → Rear Headrest</span>
        </FlowCard>
        <Arrow />
        <FlowCard label="The part is filed as">
          <span className="font-semibold text-gray-900">Headrest Front RH</span>
          <span className="mt-1 block text-xs text-gray-500">Side Right · Position Front</span>
        </FlowCard>
      </div>
    </div>
  );
}

function Terms() {
  const terms: [string, ReactNode][] = [
    ["Item type", <>Pinnacle's name for a kind of part, like <Token>CALIPER</Token>. The list on the left.</>],
    ["Mapping", "A rule that sends parts of an item type to one of our categories. Only mappings someone has confirmed are used."],
    ["Default", "A mapping with no side, position or keyword. It catches every part that no more specific mapping fits."],
    ["Side, position, keyword", "Conditions read from the part's description: LH/N/S or RH/O/S, front or rear, or any word you choose. The most specific mapping that fits wins."],
    ["Suggestion", "A possible mapping with a confidence level. Nothing happens until you press Add, and Dismiss hides it for good."],
    ["Legacy", "A mapping copied from a category's old Pinnacle name. It works as before until someone checks it."],
  ];

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900">Words used on this page</h3>
      <dl className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {terms.map(([term, meaning]) => (
          <div key={term} className="rounded-md border border-gray-200 bg-gray-50 p-3">
            <dt className="font-medium text-gray-900">{term}</dt>
            <dd className="mt-1 text-gray-600">{meaning}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Legends() {
  const items: [PinnacleItemTypeStatus, string][] = [
    ["Unmapped", "No mappings yet. Its parts are waiting for a category."],
    ["NeedsCheck", "Has a mapping copied from the old Pinnacle name that nobody has confirmed."],
    ["Mapped", "Every mapping has been confirmed."],
    ["Ignored", "Hidden from To do. Use it for item types nobody sells."],
  ];

  const parts: [PinnaclePartStatus, string][] = [
    ["Move", "Its mapping points somewhere new. Ticked for you."],
    ["Unchanged", "Already in the right category."],
    ["Listed", "Would move, but it's live on eBay. Tick it to move it anyway."],
    ["Accepted", "Would move, but someone accepted its current category. Tick it to move it anyway."],
    ["NeedsCategory", "No category and no mapping fits. Pick one in Move to, or add a mapping."],
    ["NoMatch", "Has a category, but no mapping fits it. Left where it is."],
  ];

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Item type labels</h3>
        <ul className="mt-3 space-y-2">
          {items.map(([status, meaning]) => (
            <li key={status} className="flex items-start gap-3">
              <span className="w-28 shrink-0">
                <Pill status={status} />
              </span>
              <span className="text-gray-600">{meaning}</span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Outcomes in the parts table</h3>
        <ul className="mt-3 space-y-2">
          {parts.map(([status, meaning]) => (
            <li key={status} className="flex items-start gap-3">
              <span className="w-32 shrink-0">
                <PartPill status={status} />
              </span>
              <span className="text-gray-600">{meaning}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Steps({ intro, steps }: { intro: string; steps: ReactNode[] }) {
  return (
    <div className="max-w-3xl">
      <p className="text-gray-500">{intro}</p>
      <ol className="mt-4 space-y-3">
        {steps.map((step, i) => (
          <li key={i} className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-600 text-xs font-semibold text-white">
              {i + 1}
            </span>
            <span className="pt-0.5">{step}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function FlowCard({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-[12rem] flex-1 rounded-md border border-gray-200 bg-gray-50 p-3">
      <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>
      {children}
    </div>
  );
}

function Arrow() {
  return (
    <span className="hidden items-center text-gray-400 sm:flex" aria-hidden>
      <ArrowRightIcon className="h-5 w-5" />
    </span>
  );
}

function Token({ children }: { children: ReactNode }) {
  return <span className="rounded bg-yellow-400 px-1.5 py-0.5 font-mono text-xs font-medium text-yellow-950">{children}</span>;
}

function Pill({ status }: { status: PinnacleItemTypeStatus }) {
  const meta = itemStatusMeta[status];
  return <span className={clsx("inline-block rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset", meta.className)}>{meta.label}</span>;
}

function PartPill({ status }: { status: PinnaclePartStatus }) {
  const meta = partStatusMeta[status];
  return <span className={clsx("inline-block rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset", meta.className)}>{meta.label}</span>;
}
